/**
 * This slice is dedicated to Reporting API data
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CallStatus } from '../../enums';
import { PeopleAlsoTradeItem, ReportingCall } from '../../models/reporting/types';
import { getDateTime } from '../../util/temporalDeadZoneFunctions';
import {
  ErrorPayloadFull,
  getAccountsAvailableCash,
  getAccountsAvailableCashFailed,
  getAccountsAvailableCashSuccess,
  logout,
} from '../common-actions';

import { parsedAccountTradeHistoryOrdersOrders } from './trading/order-helpers';
import { INITIAL_STATE, NO_MORE_PAGES_TO_READ } from './constants';
import { setReportingCallStatus } from './helpers';
import {
  AccountTradeHistoryPayloadData,
  AccountTradeHistoryResponseData,
  AccountTradeHistoryResponseDataWithCustomData,
  GetAccountsAvailableCashResponse,
  GetAccountTradeHistoryFirstAndLastTradePayload,
  GetAccountTradeHistoryFirstAndLastTradeResponse,
  GetOpenPositionsDataResponse,
  OpenPosition,
  PositionType,
  OpenPositionsPayloadData,
  OrderDetailResponse,
  ReportingState,
} from './types';

const reportingSlice = createSlice({
  name: 'reporting',
  initialState: INITIAL_STATE, // TODO: Remove flags for call status and use statusByCall
  reducers: {
    // TO DO: Better error handling when the reporting is implemented in web and mobile
    getOrderDetail(state: ReportingState, action: PayloadAction<string>) {
      state.orderDetailCompleted = false;
    },
    getOrderDetailSuccess(state, action: PayloadAction<OrderDetailResponse>) {
      state.orderDetailCompleted = true;
      state.orderDetail = action.payload;
    },
    getOrderDetailFailed(state: ReportingState, error) {
      state.orderDetailCompleted = true;
      state.error.hasRequestError = true;
      state.error.status = error.payload.status;
    },
    getOpenPositions(state, action: PayloadAction<OpenPositionsPayloadData>) {
      state.accountOpenPositionsCompleted = false; // TODO: Remove and use statusByCall instead
      setReportingCallStatus(ReportingCall.getOpenPositions, CallStatus.PENDING, state);
    },
    getOpenPositionsFailed(state: ReportingState, error) {
      // TODO: Remove and use statusByCall instead - start
      state.error.hasRequestError = true;
      state.accountOpenPositionsCompleted = true;
      // TODO: Remove and use statusByCall instead - end

      state.error.status = error.payload.status;
      setReportingCallStatus(ReportingCall.getOpenPositions, CallStatus.ERROR, state, error.payload.status);
    },

    getOpenPositionsSuccess(state: ReportingState, action: PayloadAction<GetOpenPositionsDataResponse>) {
      const payload = action.payload;
      const openPositions: OpenPosition[] = [];
      const accounts = payload.accounts ?? [];
      accounts.forEach(account => {
        const { accountReferenceId, openPositions: accountOpenPositions } = account ?? {};
        if (!Array.isArray(accountOpenPositions)) return;

        accountOpenPositions.forEach(position => {
          openPositions.push({
            accountReferenceId,
            symbol: position.symbol,
            currency: position.currency,
            positionType: position.positionType as PositionType,
            lastExecutionDateTime: position.lastExecutionDateTime,
            quantity: position.quantity,
            averageOpeningPrice: position.averageOpeningPrice,
            cost: position.cost,
            realizedPnl: position.realizedPnl,
          });
        });
      });

      state.openPositions = openPositions;
      state.accountOpenPositionsCompleted = true;
      setReportingCallStatus(ReportingCall.getOpenPositions, CallStatus.READY, state);
    },

    getAccountTradeHistory(state, action: PayloadAction<AccountTradeHistoryPayloadData>) {
      const { customData, isPendingOrdersCall } = (action.payload as any);
      const { queueLength } = customData ?? {};
      const theStatus = queueLength ? CallStatus.PENDING_QUEUED : CallStatus.PENDING;
      setReportingCallStatus(isPendingOrdersCall
        ? ReportingCall.getAccountPendingOrders
        : ReportingCall.getAccountTradeHistory, theStatus, state, null, customData);
    },
    getAccountTradeHistorySuccess(state, action: PayloadAction<AccountTradeHistoryResponseDataWithCustomData>) {
      const payload = action.payload;
      const { accounts = [], customData }: AccountTradeHistoryResponseDataWithCustomData = Array.isArray(payload)
        ? { accounts: [] }
        : payload;
      const items: AccountTradeHistoryResponseData[] = [];

      accounts.forEach(account => {
        const ordersList = account?.ordersList ?? [];
        items.push(...ordersList);
      });
      if (items.length > 0) {
        state.tradeHistoryNextPageToRead += 1;

        if (state.tradeHistoryNextPageToRead === items[0].pages) {
          state.tradeHistoryNextPageToRead = NO_MORE_PAGES_TO_READ;
        }
        state.tradeHistoryOrders = state.tradeHistoryOrders
          .concat(parsedAccountTradeHistoryOrdersOrders(items));
      } else {
        state.tradeHistoryOrders = [];
      }
      const { queueLength = 0 } = customData ?? {};
      const theStatus = queueLength ? null : CallStatus.READY;
      setReportingCallStatus(ReportingCall.getAccountTradeHistory, theStatus, state, null, customData);
    },
    clearAccountTradeHistory(state) {
      state.tradeHistoryOrders = [];
      state.tradeHistoryNextPageToRead = 0;
    },
    getAccountTradeHistoryFailed(state: ReportingState, error) {
      const { customData } = (error.payload);
      const { isPendingOrdersCall } = customData ?? {};
      state.error.hasRequestError = true;
      state.error.status = error.payload.status;
      setReportingCallStatus(isPendingOrdersCall ? ReportingCall.getAccountPendingOrders
        : ReportingCall.getAccountTradeHistory, CallStatus.ERROR, state, error.payload.status);
    },
    getAccountPendingOrdersSuccess(state, action: PayloadAction<AccountTradeHistoryResponseDataWithCustomData>) {
      const payload = action.payload;
      const { accounts = [], customData }: AccountTradeHistoryResponseDataWithCustomData = Array.isArray(payload)
        ? { accounts: [] }
        : payload;

      const items: AccountTradeHistoryResponseData[] = [];

      accounts.forEach(account => {
        const { ordersList = [] } = account ?? {};
        (ordersList as AccountTradeHistoryResponseData[]).forEach(order => items.push(order));
      });

      state.pendingOrders = items;
      const { queueLength = 0 } = customData ?? {};
      const theStatus = queueLength ? null : CallStatus.READY;
      setReportingCallStatus(ReportingCall.getAccountPendingOrders, theStatus, state, null, customData);
    },
    getPeopleAlsoTradeData(state, action: PayloadAction) {
      setReportingCallStatus(ReportingCall.getPeopleAlsoTradeData, CallStatus.PENDING, state);
    },
    getPeopleAlsoTradeDataCompleted(state, action: PayloadAction<PeopleAlsoTradeItem[]>) {
      state.peopleAlsoTradeData = action.payload;
      setReportingCallStatus(ReportingCall.getPeopleAlsoTradeData, CallStatus.READY, state);
    },
    getPeopleAlsoTradeDataFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setReportingCallStatus(ReportingCall.getPeopleAlsoTradeData, CallStatus.ERROR, state, status);
    },
    setHasOrderUpdate(state, action: PayloadAction<boolean>) {
      state.hasMyStocksOrderUpdate = action.payload;
    },
    getAccountTradeHistoryFirstAndLastTrade(
      state,
      action: PayloadAction<GetAccountTradeHistoryFirstAndLastTradePayload>,
    ): void {
      setReportingCallStatus(ReportingCall.getAccountTradeHistoryFirstAndLastTrade, CallStatus.PENDING, state);
    },
    getAccountTradeHistoryFirstAndLastTradeSuccess(
      state,
      action: PayloadAction<GetAccountTradeHistoryFirstAndLastTradeResponse>,
    ): void {

      // Calculate if account has trades older than 14 days
      const firstTrade = action.payload[0]?.firstTrade;
      if (firstTrade) {
        const MIN_DAYS_SINCE_FIRST_TRADE_FOR_PAYOUT = 14;
        const fourteenDaysAgo = new Date(getDateTime(
          true,
          [ 'minus' ],
          [ [ MIN_DAYS_SINCE_FIRST_TRADE_FOR_PAYOUT, 'days' ] ],
        ));
        const firstTradeDate = new Date(firstTrade);
        state.hasOlderTradeThanFourteenDays = firstTradeDate <= fourteenDaysAgo;
      }

      setReportingCallStatus(ReportingCall.getAccountTradeHistoryFirstAndLastTrade, CallStatus.READY, state);
    },
    getAccountTradeHistoryFirstAndLastTradeFailed(state: ReportingState, action: PayloadAction<any>): void {
      setReportingCallStatus(
        ReportingCall.getAccountTradeHistoryFirstAndLastTrade,
        CallStatus.ERROR,
        state,
        action.payload?.status,
      );
    },
  },
  extraReducers: {
    [logout.type]: state => ({ ...INITIAL_STATE, errorsLog: state.errorsLog }),
    [getAccountsAvailableCash.type]: (state: ReportingState) => {
      setReportingCallStatus(ReportingCall.getAvailableCash, CallStatus.PENDING, state);
    },
    [getAccountsAvailableCashSuccess.type]: (
      state: ReportingState,
      action: PayloadAction<GetAccountsAvailableCashResponse[]>,
    ) => {
      setReportingCallStatus(ReportingCall.getAvailableCash, CallStatus.READY, state);
    },
    [getAccountsAvailableCashFailed.type]: (state: ReportingState, action: PayloadAction<ErrorPayloadFull>) => {
      const { status } = action.payload ?? {} as any;
      setReportingCallStatus(ReportingCall.getAvailableCash, CallStatus.ERROR, state, status);
    },
  },
});

export const {
  getOrderDetail,
  getOrderDetailSuccess,
  getOrderDetailFailed,
  getOpenPositions,
  getOpenPositionsFailed,
  getOpenPositionsSuccess,
  getAccountTradeHistory,
  getAccountTradeHistorySuccess,
  getAccountTradeHistoryFailed,
  clearAccountTradeHistory,
  getAccountPendingOrdersSuccess,
  getPeopleAlsoTradeData,
  getPeopleAlsoTradeDataCompleted,
  getPeopleAlsoTradeDataFailed,
  setHasOrderUpdate,
  getAccountTradeHistoryFirstAndLastTrade,
  getAccountTradeHistoryFirstAndLastTradeSuccess,
  getAccountTradeHistoryFirstAndLastTradeFailed,
} = reportingSlice.actions;

export default reportingSlice.reducer;
